// src/pages/AdminNotificacoes.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Avatar,
  Grid,
  CircularProgress,
  Button,
  TextField,
  InputAdornment,
  alpha,
  useTheme,
  Divider,
  Checkbox,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";

import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import BoltIcon from "@mui/icons-material/Bolt";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import ArchiveIcon from "@mui/icons-material/Archive";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import TimelineIcon from "@mui/icons-material/Timeline";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import DeselectIcon from "@mui/icons-material/Deselect";

import { Link as RouterLink } from "react-router-dom";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  limit,
  writeBatch,
  doc,
} from "firebase/firestore";
import { db } from "../contexts/firebase/config";

function showSuccess(message) {
  enqueueSnackbar(message, { variant: "success" });
}

function showError(message) {
  enqueueSnackbar(message, { variant: "error" });
}

function showWarning(message) {
  enqueueSnackbar(message, { variant: "warning" });
}

function parseDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;

  if (typeof value === "string") {
    const partes = value.split("/");
    if (partes.length === 3) {
      const [dia, mes, ano] = partes.map(Number);
      return new Date(ano, mes - 1, dia, 23, 59, 59);
    }
  }

  const d = new Date(value);
  return isNaN(d) ? null : d;
}

function diasAte(data) {
  const d = parseDate(data);
  if (!d) return null;

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  const alvo = new Date(d);
  alvo.setHours(0, 0, 0, 0);

  return Math.ceil((alvo - hoje) / (1000 * 60 * 60 * 24));
}

function obterEstado(ref) {
  const estado = ref?.estado || "pendente";
  if (estado === "pago") return "pago";

  const validade = parseDate(ref?.validade);
  if (validade && validade < new Date()) return "expirado";

  return estado;
}

function money(value) {
  return `€ ${Number(value || 0).toFixed(2)}`;
}

function formatarData(value) {
  const d = parseDate(value);
  return d ? d.toLocaleString("pt-PT") : "—";
}

function formatarHora(value) {
  const d = parseDate(value);
  return d
    ? d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })
    : "—";
}

function normalizar(value) {
  return (value || "").toString().toLowerCase().trim();
}

function obterNomeLog(log) {
  return (
    log.clienteNome ||
    log.entidadeNome ||
    log.empresaNome ||
    log.metadata?.email ||
    "Sistema"
  );
}

function obterDetalheValor(log) {
  const partes = [];

  if (log.referencia) partes.push(`Referência: ${log.referencia}`);
  if (log.valor) partes.push(`Valor: ${money(log.valor)}`);
  if (log.empresaNome) partes.push(`Empresa: ${log.empresaNome}`);

  return partes.join(" · ");
}

function obterTipoFiltro(log) {
  const acao = (log.acao || "").toUpperCase();
  const tipo = (log.tipo || "").toLowerCase();

  if (tipo === "erro" || acao.includes("ERRO")) return "erro";
  if (tipo === "email" || acao.includes("EMAIL")) return "email";
  if (tipo === "pagamento" || acao.includes("PAGAMENTO")) return "pagamento";
  if (tipo === "cliente" || acao.includes("CLIENTE")) return "cliente";
  if (tipo === "empresa" || acao.includes("EMPRESA")) return "empresa";
  if (tipo === "compra" || acao.includes("COMPRA")) return "compra";
  if (acao.includes("REFERENCIA") || acao.includes("REFERÊNCIA")) return "pagamento";

  return "sistema";
}

function getLogUi(log) {
  const acao = (log.acao || "").toUpperCase();
  const tipo = (log.tipo || "").toUpperCase();

  if (acao.includes("PAGAMENTO_CONFIRMADO")) {
    return { title: "Pagamento confirmado", icon: <CheckCircleIcon />, color: "#16a34a" };
  }

  if (acao.includes("REVERTIDO")) {
    return { title: "Pagamento revertido", icon: <AutorenewIcon />, color: "#dc2626" };
  }

  if (acao.includes("EMAIL")) {
    return { title: "Email enviado", icon: <MarkEmailUnreadIcon />, color: "#2563eb" };
  }

  if (acao.includes("REFERENCIA") || acao.includes("REFERÊNCIA")) {
    return { title: "Referência gerada", icon: <CreditCardIcon />, color: "#d97706" };
  }

  if (acao.includes("CLIENTE_CRIADO")) {
    return { title: "Novo cliente criado", icon: <PersonIcon />, color: "#7c3aed" };
  }

  if (acao.includes("CLIENTE_ATUALIZADO")) {
    return { title: "Cliente atualizado", icon: <AutorenewIcon />, color: "#0ea5e9" };
  }

  if (acao.includes("CLIENTE_APAGADO")) {
    return { title: "Cliente removido", icon: <WarningAmberIcon />, color: "#dc2626" };
  }

  if (acao.includes("EMPRESA_CRIADA")) {
    return { title: "Empresa criada", icon: <BusinessIcon />, color: "#14b8a6" };
  }

  if (acao.includes("COMPRA_CRIADA")) {
    return { title: "Compra criada", icon: <ShoppingBagIcon />, color: "#f59e0b" };
  }

  if (tipo === "ERRO") {
    return { title: "Erro do sistema", icon: <WarningAmberIcon />, color: "#dc2626" };
  }

  return {
    title: log.titulo || "Atividade registada",
    icon: <BoltIcon />,
    color: "#0f172a",
  };
}

function agruparPorDia(logs) {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  const ontem = new Date(hoje);
  ontem.setDate(ontem.getDate() - 1);

  const grupos = {};

  logs.forEach((log) => {
    const d = parseDate(log.criadoEm);
    let label = "Sem data";

    if (d) {
      const day = new Date(d);
      day.setHours(0, 0, 0, 0);

      if (day.getTime() === hoje.getTime()) label = "Hoje";
      else if (day.getTime() === ontem.getTime()) label = "Ontem";
      else {
        label = day.toLocaleDateString("pt-PT", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        });
      }
    }

    if (!grupos[label]) grupos[label] = [];
    grupos[label].push(log);
  });

  return grupos;
}

function NotificationCard({ icon, title, subtitle, color, count, children }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "24px",
        p: 2.5,
        height: "100%",
        bgcolor: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
        border: `1px solid ${alpha(color, 0.18)}`,
        boxShadow: isDark
          ? "0 18px 45px rgba(0,0,0,0.22)"
          : "0 18px 45px rgba(15,23,42,0.055)",
      }}
    >
      <Stack direction="row" spacing={1.6} alignItems="center" sx={{ mb: 2 }}>
        <Avatar
          sx={{
            bgcolor: alpha(color, 0.12),
            color,
            width: 50,
            height: 50,
            borderRadius: "17px",
          }}
        >
          {icon}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 950, fontSize: "1rem" }}>
            {title}
          </Typography>

          <Typography variant="body2" color="text.secondary" noWrap>
            {subtitle}
          </Typography>
        </Box>

        <Chip
          label={count}
          sx={{
            height: 30,
            borderRadius: "999px",
            fontWeight: 900,
            bgcolor: alpha(color, 0.12),
            color,
          }}
        />
      </Stack>

      <Stack spacing={1.1}>{children}</Stack>
    </Paper>
  );
}

function MiniItem({ title, subtitle, chip, chipColor = "default", to }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const content = (
    <Paper
      elevation={0}
      sx={{
        p: 1.45,
        borderRadius: "16px",
        bgcolor: isDark ? "rgba(255,255,255,0.035)" : "#f8fafc",
        border: `1px solid ${
          isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)"
        }`,
        transition: "0.2s ease",
        "&:hover": {
          transform: to ? "translateY(-2px)" : "none",
          bgcolor: to ? (isDark ? "rgba(255,255,255,0.06)" : "#ffffff") : undefined,
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
        <Box sx={{ minWidth: 0 }}>
          <Typography fontWeight={900} noWrap>
            {title}
          </Typography>

          <Typography variant="body2" color="text.secondary" noWrap>
            {subtitle}
          </Typography>
        </Box>

        {chip && (
          <Chip
            label={chip}
            color={chipColor}
            size="small"
            sx={{ borderRadius: 999, fontWeight: 900, flexShrink: 0 }}
          />
        )}
      </Stack>
    </Paper>
  );

  if (!to) return content;

  return (
    <Box component={RouterLink} to={to} sx={{ textDecoration: "none", color: "inherit" }}>
      {content}
    </Box>
  );
}

function TimelineActivityItem({ log, last, selecionado, onToggle }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const ui = getLogUi(log);
  const detalheExtra = obterDetalheValor(log);
  const nome = obterNomeLog(log);

  const clienteLink = log.clienteId ? `/admin/cliente/${log.clienteId}` : null;
  const empresaLink = log.empresaId ? `/admin/empresa/${log.empresaId}/info` : null;
  const detalheLink = clienteLink || empresaLink;

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "38px 1fr", sm: "54px 1fr" }, gap: 1.4 }}>
      <Box sx={{ position: "relative", display: "flex", justifyContent: "center" }}>
        {!last && (
          <Box
            sx={{
              position: "absolute",
              top: 46,
              bottom: -18,
              width: 2,
              borderRadius: 99,
              bgcolor: alpha(ui.color, isDark ? 0.24 : 0.16),
            }}
          />
        )}

        <Avatar
          sx={{
            width: 42,
            height: 42,
            borderRadius: "16px",
            bgcolor: alpha(ui.color, 0.12),
            color: ui.color,
            border: `1px solid ${alpha(ui.color, 0.22)}`,
            boxShadow: `0 12px 28px ${alpha(ui.color, 0.14)}`,
            zIndex: 1,
          }}
        >
          {ui.icon}
        </Avatar>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.4, md: 1.8 },
          borderRadius: "22px",
          bgcolor: selecionado
            ? alpha(ui.color, isDark ? 0.13 : 0.055)
            : isDark
            ? "rgba(255,255,255,0.035)"
            : "#ffffff",
          border: `1px solid ${
            selecionado ? alpha(ui.color, 0.42) : alpha(ui.color, isDark ? 0.16 : 0.12)
          }`,
          borderLeft: `4px solid ${ui.color}`,
          transition: "0.22s ease",
          "&:hover": {
            transform: "translateY(-2px)",
            borderColor: alpha(ui.color, 0.32),
            boxShadow: isDark
              ? "0 18px 42px rgba(0,0,0,0.24)"
              : "0 18px 42px rgba(15,23,42,0.075)",
          },
        }}
      >
        <Stack direction="row" spacing={1.2} alignItems="flex-start">
          <Checkbox
            checked={selecionado}
            onChange={onToggle}
            sx={{
              p: 0.4,
              color: alpha(ui.color, 0.6),
              "&.Mui-checked": { color: ui.color },
            }}
          />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography sx={{ fontWeight: 950 }}>{ui.title}</Typography>

                  <Chip
                    label={obterTipoFiltro(log)}
                    size="small"
                    sx={{
                      height: 24,
                      borderRadius: 999,
                      fontWeight: 900,
                      bgcolor: alpha(ui.color, 0.1),
                      color: ui.color,
                      textTransform: "capitalize",
                    }}
                  />
                </Stack>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                  {nome}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8, lineHeight: 1.65 }}>
                  {log.detalhe || "Sem detalhe."}
                </Typography>

                {detalheExtra && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.6 }}>
                    {detalheExtra}
                  </Typography>
                )}
              </Box>

              <Stack alignItems={{ xs: "flex-start", md: "flex-end" }} spacing={0.8} sx={{ flexShrink: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 900, color: ui.color }}>
                  {formatarHora(log.criadoEm)}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  {formatarData(log.criadoEm)}
                </Typography>

                {detalheLink && (
                  <Button
                    component={RouterLink}
                    to={detalheLink}
                    size="small"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 900,
                    }}
                  >
                    Ver detalhes
                  </Button>
                )}
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}

export default function AdminNotificacoes() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [clientes, setClientes] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [arquivando, setArquivando] = useState(false);
  const [pesquisa, setPesquisa] = useState("");
  const [filtro, setFiltro] = useState("todos");
  const [selecionados, setSelecionados] = useState([]);

  const ui = {
    bg: isDark ? "#020617" : "#f6f8fc",
    card: isDark ? "rgba(15,23,42,0.86)" : "#ffffff",
    soft: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
    border: isDark ? "rgba(255,255,255,0.09)" : "rgba(15,23,42,0.08)",
    text: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
  };

  function limparFiltros() {
    setPesquisa("");
    setFiltro("todos");
    setSelecionados([]);
    showSuccess("Filtros limpos.");
  }

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "clientes"),
      (snap) => {
        setClientes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingClientes(false);
      },
      () => setLoadingClientes(false)
    );

    return unsub;
  }, []);

  useEffect(() => {
    const q = query(collection(db, "atividadeLogs"), orderBy("criadoEm", "desc"), limit(80));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((log) => log.arquivado !== true);

        setLogs(list);
        setLoadingLogs(false);
      },
      (err) => {
        console.error(err);
        setLoadingLogs(false);
      }
    );

    return unsub;
  }, []);

  const logsFiltrados = useMemo(() => {
    const termo = normalizar(pesquisa);

    return logs
      .filter((log) => {
        if (filtro === "todos") return true;
        return obterTipoFiltro(log) === filtro;
      })
      .filter((log) => {
        if (!termo) return true;

        return (
          normalizar(log.acao).includes(termo) ||
          normalizar(log.titulo).includes(termo) ||
          normalizar(log.detalhe).includes(termo) ||
          normalizar(log.clienteNome).includes(termo) ||
          normalizar(log.empresaNome).includes(termo) ||
          normalizar(log.entidadeNome).includes(termo) ||
          normalizar(log.referencia).includes(termo) ||
          normalizar(log.tipo).includes(termo)
        );
      })
      .slice(0, 28);
  }, [logs, pesquisa, filtro]);

  const logsAgrupados = useMemo(() => agruparPorDia(logsFiltrados), [logsFiltrados]);

  const idsVisiveis = useMemo(() => logsFiltrados.map((log) => log.id), [logsFiltrados]);

  function toggleSelecionado(id) {
    setSelecionados((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  }

  function selecionarTodosVisiveis() {
    setSelecionados(idsVisiveis);
    showSuccess("Atividades visíveis selecionadas.");
  }

  function limparSelecionados() {
    setSelecionados([]);
    showSuccess("Seleção limpa.");
  }

  async function arquivarSelecionados() {
    if (selecionados.length === 0) {
      showWarning("Seleciona pelo menos uma atividade.");
      return;
    }

    if (!window.confirm(`Arquivar ${selecionados.length} atividade(s) selecionada(s)?`)) {
      return;
    }

    try {
      setArquivando(true);

      const batch = writeBatch(db);

      selecionados.forEach((id) => {
        const ref = doc(db, "atividadeLogs", id);

        batch.update(ref, {
          arquivado: true,
          arquivadoEm: new Date(),
        });
      });

      await batch.commit();

      setSelecionados([]);
      showSuccess("Atividades arquivadas com sucesso.");
    } catch (error) {
      console.error(error);
      showError("Erro ao arquivar atividades.");
    } finally {
      setArquivando(false);
    }
  }

  const renovacoes15 = useMemo(() => {
    return clientes
      .map((c) => ({ ...c, dias: diasAte(c.dataFim) }))
      .filter((c) => c.dias !== null)
      .filter((c) => c.dias >= 0 && c.dias <= 15)
      .sort((a, b) => a.dias - b.dias);
  }, [clientes]);

  const pagamentosPendentes = useMemo(() => {
    return clientes.filter((c) => obterEstado(c.referenciaPagamento) === "pendente");
  }, [clientes]);

  const pagamentosExpirados = useMemo(() => {
    return clientes.filter((c) => obterEstado(c.referenciaPagamento) === "expirado");
  }, [clientes]);

  const clientesSemEmail = useMemo(() => {
    return clientes.filter((c) => !c.email);
  }, [clientes]);

  const pagamentosConfirmados = useMemo(() => {
    return clientes
      .filter((c) => obterEstado(c.referenciaPagamento) === "pago")
      .slice(0, 8);
  }, [clientes]);

  const loading = loadingClientes || loadingLogs;

  if (loading) {
    return (
      <Box sx={{ minHeight: "60vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100%", bgcolor: ui.bg, p: { xs: 0.5, md: 1 } }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={2.5}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography sx={{ fontSize: 14, color: ui.muted, fontWeight: 800 }}>
            Centro administrativo
          </Typography>

          <Typography
            sx={{
              mt: 0.7,
              fontSize: { xs: "2rem", md: "2.75rem" },
              fontWeight: 950,
              letterSpacing: "-0.06em",
              lineHeight: 1,
              color: ui.text,
            }}
          >
            Notificações
          </Typography>

          <Typography sx={{ mt: 1.2, color: ui.muted }}>
            Alertas, pagamentos e últimas atualizações da plataforma.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="flex-start">
          <Chip
            icon={<TimelineIcon />}
            label={`${logsFiltrados.length} atividades`}
            sx={{
              height: 42,
              borderRadius: "14px",
              fontWeight: 900,
              bgcolor: ui.card,
              border: `1px solid ${ui.border}`,
            }}
          />

          <Chip
            icon={<WarningAmberIcon />}
            label={`${renovacoes15.length} urgentes`}
            sx={{
              height: 42,
              borderRadius: "14px",
              fontWeight: 900,
              bgcolor: alpha(theme.palette.error.main, 0.08),
              border: `1px solid ${alpha(theme.palette.error.main, 0.18)}`,
              color: theme.palette.error.main,
            }}
          />
        </Stack>
      </Stack>

      <Grid container spacing={2.2}>
        <Grid item xs={12} md={6}>
          <NotificationCard
            icon={<WarningAmberIcon />}
            title="Renovações urgentes"
            subtitle="Clientes a renovar nos próximos 15 dias."
            color="#dc2626"
            count={renovacoes15.length}
          >
            {renovacoes15.length === 0 ? (
              <Typography color="text.secondary">Sem renovações urgentes.</Typography>
            ) : (
              renovacoes15.slice(0, 8).map((c) => (
                <MiniItem
                  key={c.id}
                  title={c.nome || "Cliente"}
                  subtitle={`Plano: ${c.plano || "—"}`}
                  chip={`${c.dias} dias`}
                  chipColor={c.dias <= 7 ? "error" : "warning"}
                  to={`/admin/cliente/${c.id}`}
                />
              ))
            )}
          </NotificationCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <NotificationCard
            icon={<CreditCardIcon />}
            title="Pagamentos pendentes"
            subtitle="Referências que ainda aguardam pagamento."
            color="#d97706"
            count={pagamentosPendentes.length}
          >
            {pagamentosPendentes.length === 0 ? (
              <Typography color="text.secondary">Sem pagamentos pendentes.</Typography>
            ) : (
              pagamentosPendentes.slice(0, 8).map((c) => (
                <MiniItem
                  key={c.id}
                  title={c.nome || "Cliente"}
                  subtitle={money(c.referenciaPagamento?.valor)}
                  chip="Pendente"
                  chipColor="warning"
                  to={`/admin/cliente/${c.id}`}
                />
              ))
            )}
          </NotificationCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <NotificationCard
            icon={<EventBusyIcon />}
            title="Referências expiradas"
            subtitle="Pagamentos cuja validade expirou."
            color="#b91c1c"
            count={pagamentosExpirados.length}
          >
            {pagamentosExpirados.length === 0 ? (
              <Typography color="text.secondary">Sem referências expiradas.</Typography>
            ) : (
              pagamentosExpirados.slice(0, 8).map((c) => (
                <MiniItem
                  key={c.id}
                  title={c.nome || "Cliente"}
                  subtitle={`Ref: ${c.referenciaPagamento?.referencia || "—"}`}
                  chip="Expirada"
                  chipColor="error"
                  to={`/admin/cliente/${c.id}`}
                />
              ))
            )}
          </NotificationCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <NotificationCard
            icon={<MarkEmailUnreadIcon />}
            title="Clientes sem email"
            subtitle="Clientes que não possuem email associado."
            color="#2563eb"
            count={clientesSemEmail.length}
          >
            {clientesSemEmail.length === 0 ? (
              <Typography color="text.secondary">Todos os clientes têm email.</Typography>
            ) : (
              clientesSemEmail.slice(0, 8).map((c) => (
                <MiniItem
                  key={c.id}
                  title={c.nome || "Cliente"}
                  subtitle={`NIF: ${c.nif || "—"}`}
                  chip="Sem email"
                  to={`/admin/cliente/${c.id}`}
                />
              ))
            )}
          </NotificationCard>
        </Grid>

        <Grid item xs={12}>
          <NotificationCard
            icon={<CheckCircleIcon />}
            title="Pagamentos confirmados"
            subtitle="Últimos pagamentos marcados como pagos."
            color="#16a34a"
            count={pagamentosConfirmados.length}
          >
            {pagamentosConfirmados.length === 0 ? (
              <Typography color="text.secondary">
                Ainda não existem pagamentos confirmados.
              </Typography>
            ) : (
              pagamentosConfirmados.map((c) => (
                <MiniItem
                  key={c.id}
                  title={c.nome || "Cliente"}
                  subtitle={money(c.referenciaPagamento?.valor)}
                  chip="Pago"
                  chipColor="success"
                  to={`/admin/cliente/${c.id}`}
                />
              ))
            )}
          </NotificationCard>
        </Grid>

        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "28px",
              p: { xs: 2.2, md: 2.8 },
              bgcolor: ui.card,
              border: `1px solid ${ui.border}`,
              boxShadow: isDark
                ? "0 18px 55px rgba(0,0,0,0.18)"
                : "0 18px 45px rgba(15,23,42,0.055)",
            }}
          >
            <Stack
              direction={{ xs: "column", lg: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", lg: "center" }}
              spacing={2}
              sx={{ mb: 2.4 }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                    width: 52,
                    height: 52,
                    borderRadius: "18px",
                  }}
                >
                  <RadioButtonCheckedIcon />
                </Avatar>

                <Box>
                  <Typography sx={{ fontWeight: 950, fontSize: "1.25rem" }}>
                    Timeline de atividade
                  </Typography>

                  <Typography color="text.secondary">
                    Seleciona apenas as atividades que queres arquivar.
                  </Typography>
                </Box>
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label={`${selecionados.length} selecionada(s)`}
                  sx={{ borderRadius: 999, fontWeight: 900 }}
                />

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<SelectAllIcon />}
                  onClick={selecionarTodosVisiveis}
                  disabled={logsFiltrados.length === 0}
                  sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
                >
                  Selecionar visíveis
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DeselectIcon />}
                  onClick={limparSelecionados}
                  disabled={selecionados.length === 0}
                  sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
                >
                  Limpar seleção
                </Button>

                <Button
                  component={RouterLink}
                  to="/admin/notificacoes/arquivadas"
                  variant="contained"
                  startIcon={<ArchiveIcon />}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 900,
                  }}
                >
                  Ver arquivadas
                </Button>

                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<ArchiveIcon />}
                  onClick={arquivarSelecionados}
                  disabled={arquivando || selecionados.length === 0}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 900,
                    bgcolor: ui.card,
                  }}
                >
                  {arquivando ? "A arquivar..." : `Arquivar (${selecionados.length})`}
                </Button>
              </Stack>
            </Stack>

            <Paper
              elevation={0}
              sx={{
                p: 1.6,
                mb: 2.4,
                borderRadius: "22px",
                bgcolor: ui.soft,
                border: `1px solid ${ui.border}`,
              }}
            >
              <Stack spacing={1.4}>
                <TextField
                  fullWidth
                  value={pesquisa}
                  onChange={(e) => {
                    setPesquisa(e.target.value);
                    setSelecionados([]);
                  }}
                  placeholder="Pesquisar por cliente, empresa, ação, referência ou detalhe..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "18px",
                      bgcolor: isDark ? "rgba(255,255,255,0.035)" : "#ffffff",
                    },
                  }}
                />

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {[
                    ["todos", "Todos"],
                    ["cliente", "Clientes"],
                    ["pagamento", "Pagamentos"],
                    ["email", "Emails"],
                    ["empresa", "Empresas"],
                    ["compra", "Compras"],
                    ["erro", "Erros"],
                    ["sistema", "Sistema"],
                  ].map(([value, label]) => (
                    <Chip
                      key={value}
                      label={label}
                      clickable
                      onClick={() => {
                        setFiltro(value);
                        setSelecionados([]);
                      }}
                      color={filtro === value ? "primary" : "default"}
                      sx={{ borderRadius: 999, fontWeight: 900 }}
                    />
                  ))}

                  {(pesquisa || filtro !== "todos") && (
                    <Button
                      variant="outlined"
                      startIcon={<ClearIcon />}
                      onClick={limparFiltros}
                      sx={{
                        borderRadius: 999,
                        textTransform: "none",
                        fontWeight: 900,
                        bgcolor: ui.card,
                      }}
                    >
                      Limpar filtros
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Paper>

            {logsFiltrados.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "20px",
                  bgcolor: ui.soft,
                  border: `1px dashed ${ui.border}`,
                  textAlign: "center",
                }}
              >
                <Typography fontWeight={900}>Nenhuma atividade encontrada.</Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Tenta limpar os filtros ou pesquisar por outro termo.
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={3}>
                {Object.entries(logsAgrupados).map(([dia, items]) => (
                  <Box key={dia}>
                    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.6 }}>
                      <Chip
                        label={dia}
                        sx={{
                          borderRadius: 999,
                          fontWeight: 950,
                          bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9",
                        }}
                      />
                      <Divider sx={{ flex: 1 }} />
                    </Stack>

                    <Stack spacing={1.8}>
                      {items.map((log, index) => (
                        <TimelineActivityItem
                          key={log.id}
                          log={log}
                          last={index === items.length - 1}
                          selecionado={selecionados.includes(log.id)}
                          onToggle={() => toggleSelecionado(log.id)}
                        />
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}